import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, limit, startAfter } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';

export default function AdminLogViewer() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('all');

  const fetchLogs = async (isLoadMore = false) => {
    try {
      let q = query(
        collection(db, 'adminLogs'),
        orderBy('timestamp', 'desc'),
        limit(20)
      );

      if (isLoadMore && lastDoc) {
        q = query(
          collection(db, 'adminLogs'),
          orderBy('timestamp', 'desc'),
          startAfter(lastDoc),
          limit(20)
        );
      }

      const snapshot = await getDocs(q);
      const newLogs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate()
      }));

      setHasMore(newLogs.length === 20);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1]);

      if (isLoadMore) {
        setLogs(prev => [...prev, ...newLogs]);
      } else {
        setLogs(newLogs);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const getActionTypeLabel = (actionType) => {
    const labels = {
      'transaction.revert': 'Tranzakció visszavonás',
      'transaction.transfer': 'Utalás',
      'transaction.batch_update': 'Tömeges tranzakció művelet',
      'user.import': 'Felhasználó importálás',
      'user.update': 'Felhasználó módosítás',
      'user.batch_update': 'Tömeges felhasználó művelet'
    };
    return labels[actionType] || actionType;
  };

  const filteredLogs = logs.filter(log => {
    const term = searchTerm.toLowerCase();
    
    switch(searchType) {
      case 'admin':
        return log.adminEmail?.toLowerCase().includes(term);
      case 'action':
        return log.actionType?.toLowerCase().includes(term) || 
               getActionTypeLabel(log.actionType)?.toLowerCase().includes(term);
      case 'ip':
        return log.meta?.ipAddress?.toLowerCase().includes(term);
      default:
        return log.adminEmail?.toLowerCase().includes(term) ||
               log.actionType?.toLowerCase().includes(term) ||
               getActionTypeLabel(log.actionType)?.toLowerCase().includes(term) ||
               log.meta?.ipAddress?.toLowerCase().includes(term) ||
               JSON.stringify(log.details)?.toLowerCase().includes(term);
    }
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Icons.Loader className="animate-spin h-8 w-8 text-gray-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Admin Műveletek</h1>
        <p className="mt-1 text-sm text-gray-500">Rendszerműveletek naplója</p>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 flex gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Keresés..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            />
            <Icons.Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          <select
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="all">Minden mező</option>
            <option value="admin">Admin</option>
            <option value="action">Művelet</option>
            <option value="ip">IP Cím</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <div className="text-sm text-gray-500">
            {filteredLogs.length} találat
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Időpont
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Admin
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Művelet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Részletek
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  IP Cím
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLogs.map((log) => (
                <tr key={log.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {log.timestamp?.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{log.adminEmail}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {getActionTypeLabel(log.actionType)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                      {JSON.stringify(log.details, null, 2)}
                    </pre>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{log.meta?.ipAddress}</div>
                    <div className="text-xs text-gray-400">{log.meta?.platform}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {hasMore && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={() => fetchLogs(true)}
            className="px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md"
          >
            További logok betöltése
          </button>
        </div>
      )}
    </div>
  );
} 